import { organizationCodec } from '@/services/api/resources/organization';
import * as t from 'io-ts';
import { withFallback } from 'io-ts-types/lib/withFallback';
import { id, role } from '@/services/api';
import { formLocalesUnionType, localesUnionType } from '@/types';
import { quota } from '@/services/api/resources/subscription/codec';
import { subscriptionCodec } from '@/services/api/resources/chargebee/subscription/codec';
import { DateFromUnixTime } from 'io-ts-types/DateFromUnixTime';
import { featuresCodec } from '@/services/api/resources/features';

interface Tier {
  unit_amount: string | null;
  up_to: number | null;
}

export const tier = t.type(({
  unit_amount: t.union([t.string, t.null]),
  up_to: t.union([t.number, t.null]),
}));

interface LegacySubscription {
  name: string;
  status: string;
  quantity: number;
  interval: string;
  organization_id: number;
  users: {
    count: number;
    initial_count: number;
    limit: number;
    limit_reached: boolean;
  };
  trial_ends_at: number | null;
  next_charge_at: number | null;
  next_charge_amount: string;
  canceled_at: number | null;
  ends_at: number | null;
  is_free: boolean;
  plan: {
    name: string;
    slug: string;
    is_free: boolean;
    nickname: string | null;
    currency: string;
    interval: string;
    active: number;
    amount: string;
    trial_period_days: number | null;
    tiers: Array<Tier>;
  };
  billing_details: {
    name: string | null;
    email: string | null;
    card: {
      number: string | null;
      expiry: string | null;
      brand: string | null;
    };
    address: string | null;
    zip: string | null;
    city: string | null;
    country: string | null;
    vat: string | null;
  };
  next_subscription: Array<LegacySubscription> | null | undefined;
}

const LegacySubscription: t.Type<LegacySubscription> = t.recursion('LegacySubscription', () =>
  t.type({
    name: t.string,
    status: t.string,
    quantity: t.number,
    interval: t.string,
    organization_id: t.number,
    users: t.type({
      count: t.number,
      initial_count: t.number,
      limit: t.number,
      limit_reached: t.boolean,
    }),
    trial_ends_at: t.union([t.number, t.null]),
    next_charge_at: t.union([t.number, t.null]),
    next_charge_amount: t.string,
    canceled_at: t.union([t.number, t.null]),
    ends_at: t.union([t.number, t.null]),
    is_free: t.boolean,
    plan: t.type({
      name: t.string,
      slug: t.string,
      is_free: t.boolean,
      nickname: t.union([t.string, t.null]),
      currency: t.string,
      interval: t.string,
      active: t.number,
      amount: t.string,
      trial_period_days: t.union([t.number, t.null]),
      tiers: t.array(tier),
    }),
    billing_details: t.type({
      name: t.union([t.string, t.null]),
      email: t.union([t.string, t.null]),
      card: t.type({
        number: t.union([t.string, t.null]),
        expiry: t.union([t.string, t.null]),
        brand: t.union([t.string, t.null]),
      }),
      address: t.union([t.string, t.null]),
      zip: t.union([t.string, t.null]),
      city: t.union([t.string, t.null]),
      country: t.union([t.string, t.null]),
      vat: t.union([t.string, t.null]),
    }),
    next_subscription: t.union([t.array(LegacySubscription), t.null, t.undefined]),
  }));

export const user = t.type({
  id,
  name: t.string,
  phoneNumber: t.union([t.string, t.null]),
  email: t.string,
  createdAt: DateFromUnixTime,
  locale: localesUnionType,
  permissions: t.array(t.string),
  admin: t.boolean,
  role: t.union([role, t.null]),
  timezone: withFallback(t.string, ''),
  timezoneId: withFallback(t.string, ''),
  unreadNotifications: t.number,
  organization: organizationCodec,
  features: featuresCodec,
  analytics: t.type({
    id: t.union([t.string, t.null]),
    issuesCreated: t.number,
    completedInspections: t.number,
  }),
  entityItems: t.array(t.type({
    id: t.number,
    title: t.string,
  })),
  appDownloaded: t.boolean,
  shownTours: t.array(t.union([t.string, t.number])),
  _legacy_has_hardware: withFallback(t.number, 0),
  _legacy_subscription: t.union([t.null, LegacySubscription]),
  _legacy_pays_by_invoice: t.number,
  subscriberAccount: t.union([t.null, t.string]),
  subscriberPlatform: t.union([t.null, t.string]),
  subscription: t.union([t.null, subscriptionCodec]),
  subscriptionFeatures: t.array(t.string),
  subscriptionQuotas: t.array(quota),
  formTranslationsLocale: withFallback(t.union([formLocalesUnionType, t.null]), null),
  aiWallet: t.type({
    monthly: withFallback(t.number, 0),
    available: withFallback(t.number, 0),
  }),
});
